import clsx from "clsx";
import ScannerLink, { ScannerType } from "components/ScannerLink";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { SourceChainContext } from "hooks/sourceChain";
import chainIdMap from "lib/chainIdMap";
import CHAINS, { DestinationChain, DESTINATION_CHAINS } from "lib/chains";
import { Chain } from "lib/types";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties, MouseEvent, useContext, useMemo } from "react";
import { BiLoaderAlt } from "react-icons/bi";
import { ExecutedMessage, SentMessage } from "../../../.graphclient";

const override: CSSProperties = {
  display: "block",
  margin: "0 auto",
  borderColor: "red",
};

function LoadingText({ children }: { children: string }) {
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
      }}
    >
      <BiLoaderAlt size="20px" className="mb-1 animate-spin" />
      &nbsp; {children}
    </span>
  );
}

export default function MessageRow({
  messageNonce,
  sourceTransaction,
  targetTransaction,
  latestHeadUpdates,
  lastFinalizedBlock,
}: {
  messageNonce: number;
  sourceTransaction: SentMessage | null;
  targetTransaction: ExecutedMessage | null;
  latestHeadUpdates: Map<DestinationChain, number>;
  lastFinalizedBlock: number | null;
}) {
  const router = useRouter();
  const sourceChain = useContext(SourceChainContext);
  const { messageReceiverChainID, messageSender, messageReceiver } =
    sourceTransaction || {};
  const sourceTimestamp = useMemo(
    () => fromUnixTime(sourceTransaction?.blockTimestamp),
    [sourceTransaction]
  );
  const targetTimestamp = useMemo(
    () => fromUnixTime(targetTransaction?.blockTimestamp),
    [targetTransaction]
  );
  const targetChain = chainIdMap.get(parseInt(messageReceiverChainID));
  const targetChainName =
    targetChain !== undefined
      ? CHAINS[targetChain]?.name
      : `Unknown (${messageReceiverChainID})`;

  const missingTargetTxnStatus = useMemo(() => {
    if (targetTransaction === null) {
      if (!DESTINATION_CHAINS[sourceChain].includes(targetChain as any)) {
        return "Unindexed chain";
      }
      if (latestHeadUpdates.has(targetChain as DestinationChain)) {
        if (
          latestHeadUpdates.get(targetChain as DestinationChain)! <
          sourceTransaction!.blockNumber
        ) {
          if (
            lastFinalizedBlock !== null &&
            lastFinalizedBlock < sourceTransaction?.blockNumber
          ) {
            return <LoadingText>Waiting for block to finalize</LoadingText>;
          } else {
            return <LoadingText>Waiting for proof of consensus</LoadingText>;
          }
        } else {
          return <LoadingText>Waiting for relayer</LoadingText>;
        }
      } else {
        return "Transaction not found";
      }
    } else {
      return null;
    }
  }, [
    targetTransaction,
    lastFinalizedBlock,
    latestHeadUpdates,
    sourceTransaction,
    sourceChain,
    targetChain,
  ]);

  if (sourceTransaction === null) {
    return (
      <tr>
        <td className="py-4 px-3">
          <Link href={`/nonce/${messageNonce}`} className="text-succinct-neon">
            {messageNonce}
          </Link>
        </td>
        <td colSpan={7} className="py-4 px-3">
          Missing from subgraph
        </td>
      </tr>
    );
  }

  const onClickRow = (e: MouseEvent) => {
    if (e.target instanceof HTMLAnchorElement) {
      return;
    }
    router.push(`/nonce/${messageNonce}`);
  };

  return (
    <tr
      className="min-w-full cursor-pointer bg-succinct-teal-5 text-white hover:bg-succinct-teal-10"
      onClick={onClickRow}
    >
      <td className="px-3 font-mono text-succinct-teal">
        {/* <Link
          href={`/msg/${messageNonce}`}
          className="ibm-plex-regular font-mono text-succinct-teal hover:underline"
        > */}
        {messageNonce}
        {/* </Link> */}
      </td>
      <td className="py-4 px-3">{targetChainName}</td>
      <td className="py-4 px-3">
        <ScannerLink
          value={sourceTransaction?.transactionOrigin}
          chain={sourceChain}
          type={ScannerType.Address}
          shorten={true}
        />
      </td>
      <td className="py-4 px-3">
        <ScannerLink
          value={messageSender}
          chain={sourceChain}
          type={ScannerType.Address}
          shorten={true}
        />
      </td>
      <td className="py-4 px-3">
        <ScannerLink
          value={messageReceiver}
          chain={targetChain || ("mainnet" as Chain)}
          type={ScannerType.Address}
          shorten={true}
        />
      </td>
      <td className="py-4 px-3">
        <ScannerLink
          value={sourceTransaction.transactionHash}
          chain={sourceChain}
          type={ScannerType.Tx}
          shorten={true}
        />
      </td>
      <td className="py-4 px-3">
        <span title={sourceTimestamp.toLocaleString()}>
          {formatDistanceToNowStrict(sourceTimestamp, {
            addSuffix: true,
          })}
        </span>
      </td>
      {targetTransaction === null ? (
        <td className="py-4 px-3" colSpan={2}>
          {missingTargetTxnStatus}
        </td>
      ) : (
        <>
          <td className="py-4 px-3">
            <ScannerLink
              value={targetTransaction.transactionHash}
              chain={chainIdMap.get(parseInt(messageReceiverChainID))}
              type={ScannerType.Tx}
              shorten={true}
            />
          </td>
          <td className="py-4 px-3">
            <span title={targetTimestamp.toLocaleString()}>
              {formatDistanceToNowStrict(targetTimestamp, {
                addSuffix: true,
              })}
            </span>
          </td>
        </>
      )}
      {/* <td className="w-[20px]">
        <Link href={`/msg/${messageNonce}`} className="h-[55px]">
          <div className="h-full">
            <BiRightArrowAlt size="30px" />
          </div>
        </Link>
      </td> */}
    </tr>
  );
}
